import type { AuditEntry } from '../types/audit-log.types';

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

function flatten(value: unknown, prefix = ''): Map<string, string> {
  const out = new Map<string, string>();
  if (value === null || value === undefined) return out;
  if (typeof value !== 'object' || Array.isArray(value)) {
    out.set(prefix || 'value', JSON.stringify(value as Json));
    return out;
  }
  for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
      for (const [k, v] of flatten(inner, path)) out.set(k, v);
    } else {
      out.set(path, JSON.stringify(inner as Json));
    }
  }
  return out;
}

/**
 * What changed, field by field: the recorded before and after side by side.
 * Values are shown exactly as recorded — the audit writer never records codes
 * or credentials, so nothing here needs masking.
 */
export function AuditChange({ entry }: { entry: AuditEntry }) {
  const before = flatten(entry.before);
  const after = flatten(entry.after);
  const fields = [...new Set([...before.keys(), ...after.keys()])];

  if (fields.length === 0) {
    return <p className="text-muted-foreground text-xs">No field values were recorded.</p>;
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          <th className="py-1 pe-3 text-start font-medium">Field</th>
          <th className="py-1 pe-3 text-start font-medium">Before</th>
          <th className="py-1 text-start font-medium">After</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => {
          const was = before.get(field);
          const now = after.get(field);
          return (
            <tr key={field} className="border-t align-top">
              <td className="py-1 pe-3 font-medium">{field}</td>
              <td className="text-muted-foreground py-1 pe-3 font-mono break-all">{was ?? '—'}</td>
              <td
                className={
                  was !== now
                    ? 'py-1 font-mono break-all'
                    : 'text-muted-foreground py-1 font-mono break-all'
                }
              >
                {now ?? '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
